import { Radio as UikitRadio } from "@circle-react-shared/uikit/Radio";
import type { Plan } from "@circle-react/types/Plan";
import { Radio } from "@headlessui/react-v2";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { usePlans } from "@/react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { isCurrent } from "@/react/helpers/plansHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useTailwindSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { usePlanPageVariant } from "@/react/hooks/usePlanPageVariant";

import { AnnualDiscount } from "../shared/AnnualDiscount";
import { PlanCardHighlights } from "./PlanCardHighlights";
import { PlanMonthlyPrice } from "./PlanMonthlyPrice";

interface PlanCardProps {
  onClick: () => void;
  plan: Plan;
  selected: boolean;
}

const tierDescriptionTranslations: Partial<Record<Plan["tier"], () => string>> =
  {
    basic: () =>
      t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.tier_descriptions.basic",
      ),
    professional: () =>
      t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.tier_descriptions.professional",
      ),
    business: () =>
      t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.tier_descriptions.business",
      ),
    enterprise: () =>
      t(
        "pricing_plans.plan_settings.plan_selection.plan_selector.tier_descriptions.enterprise",
      ),
  };

export const PlanCard = ({ plan, selected, onClick }: PlanCardProps) => {
  const isCurrentPlan = isCurrent(plan.plan_action);
  const isSmOrMdScreen = useTailwindSmScreenMediaQuery();

  const { currentPlan, tierDescriptions } = usePlans();

  const isTrialingThisPlan =
    currentPlan?.plan_trial?.active &&
    currentPlan?.plan_trial?.tier === plan.tier;
  const { isDesktopLayout, isCompactLayout } = usePlanPageVariant();

  const tierDescription = (
    <Typography.BodySm weight="font-normal">
      {tierDescriptions?.[plan.tier] ??
        tierDescriptionTranslations[plan.tier]?.() ??
        ""}
    </Typography.BodySm>
  );

  return (
    <Radio
      key={plan.tier}
      value={plan.tier}
      as="div"
      className={classNames(
        "border-primary cursor-pointer border-b p-4 last:border-none sm:p-6",
        selected ? "bg-gray-50 dark:bg-gray-900" : "bg-primary",
      )}
      onClick={onClick}
    >
      <div className="flex flex-row justify-between">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="flex items-start">
              <UikitRadio
                value={plan.tier}
                label=""
                checked={selected}
                onChange={() => {}}
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-row items-center gap-1">
                <Typography.LabelLg weight="semibold">
                  {plan.display_name}
                </Typography.LabelLg>
                {isCurrentPlan && (
                  <Badge
                    label={t(
                      "pricing_plans.plan_settings.plan_selection.plan_selector.current_plan",
                    )}
                    square
                    variant="secondary"
                  />
                )}
                {plan.is_on_offer &&
                  !isCurrentPlan &&
                  plan.plan_coupon?.display_discount_percentage && (
                    <Badge
                      label={t(
                        "pricing_plans.plan_settings.plan_selection.plan_selector.plan_card.discount_percentage",
                        {
                          percentage:
                            plan.plan_coupon.display_discount_percentage,
                        },
                      )}
                      square
                      variant="indigo"
                    />
                  )}
                {isTrialingThisPlan && (
                  <Badge
                    label={t(
                      "pricing_plans.plan_settings.plan_selection.plan_selector.trialing_plan",
                    )}
                    square
                    truncate={false}
                    className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    variant="info"
                  />
                )}
              </div>
              {isSmOrMdScreen && (
                <div className="hidden sm:block">{tierDescription}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end">
          {!isCompactLayout && <PlanMonthlyPrice plan={plan} />}
          {isSmOrMdScreen && !isDesktopLayout && (
            <div className="hidden sm:block">
              <AnnualDiscount plan={plan} />
            </div>
          )}
        </div>
      </div>
      <div
        className={classNames(
          "grid overflow-hidden transition-all duration-300 ease-in-out",
          selected ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 px-7 sm:px-0">
          {!isSmOrMdScreen && (
            <div className="mt-2 block sm:hidden">
              <div className="mt-1">{tierDescription}</div>
            </div>
          )}
          <PlanCardHighlights plan={plan} />
        </div>
      </div>
    </Radio>
  );
};
